"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CommandDialog = CommandDialog;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_dom_1 = require("react-dom");
const cn_1 = require("../lib/cn");
const overlay_1 = require("../lib/overlay");
const icon_1 = require("./icon");
const folded_1 = require("../lib/folded");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⌘K. Keyboard-first navigation across the whole app.                        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **A combobox, not a menu**, and the roles are not interchangeable: the input is
 * `role="combobox"` with `aria-activedescendant` pointing into a `role="listbox"`. A menu has no
 * text field, so a screen reader in menu mode swallows the typing.
 *
 * ⚠️ **Focus stays in the input; the highlight moves.** That is the whole point of a palette —
 * arrowing must not take focus off the field or the next keystroke goes nowhere.
 *
 * ⚠️ **The highlight index counts across GROUPS, not within them.** The design system's
 * implementation increments a shared counter as it renders, which works and is fragile: it
 * depends on render order matching the filter order. Here the flat list is computed first and
 * the groups are a view over it, so the index and the item can't disagree.
 *
 * ⚠️ **Scrolling the active row into view is manual**, because the list scrolls and the browser
 * will not do it for an element that never receives focus. Without it, arrowing past the eighth
 * item highlights rows nobody can see.
 */
function CommandDialog({ open, onOpenChange, items, placeholder, label, emptyLabel, loading = false, loadingLabel, onQueryChange, }) {
    const [query, setQuery] = (0, react_1.useState)('');
    const [active, setActive] = (0, react_1.useState)(0);
    /** ⚠️ See the scroll effect below: only a key press may move the list. */
    const byKeyboard = (0, react_1.useRef)(false);
    const scope = (0, react_1.useId)();
    /** ⚠️ See the reset effect below for why this is a ref. */
    const notify = (0, react_1.useRef)(onQueryChange);
    notify.current = onQueryChange;
    const list = (0, react_1.useRef)(null);
    const close = () => onOpenChange?.(false);
    const { ref } = (0, overlay_1.useModalSurface)(open, close);
    /* ⚠️ 17 Oct 2026 · accents folded (`folded`, as `Select` and the list toolbar do), so
       `szabadsag` finds *Szabadság* typed on a keyboard that is not Hungarian. */
    const matches = query === ''
        ? items
        : items.filter((item) => (0, folded_1.folded)(`${item.label} ${item.hint ?? ''} ${item.group ?? ''}`).includes((0, folded_1.folded)(query.trim())));
    /* Reset on open, so a palette reopened after a search does not start mid-filter. */
    (0, react_1.useEffect)(() => {
        if (open) {
            setQuery('');
            setActive(0);
            /* ⚠️ The caller is told too, or it would keep the last search's results behind an empty
               box — which is what somebody sees for the whole of the first keystroke. */
            notify.current?.('');
        }
        /* ⚠️⚠️ **`notify` IS A REF, NOT A DEPENDENCY, AND THAT IS WHAT KEEPS THIS EFFECT HONEST.**
           `onQueryChange` is a fresh closure on every render of the caller, so depending on it would
           re-run this effect constantly and clear the field under somebody's hands — and omitting it
           from the array while calling it would be a stale-closure bug waiting for the first caller
           whose handler changes. A ref is neither: the effect depends on `open` alone, truthfully, and
           always calls the current handler. */
    }, [open]);
    (0, react_1.useEffect)(() => {
        setActive(0);
    }, [query]);
    /**
     * ⚠️⚠️ **THE KEYBOARD SCROLLS, THE POINTER DOES NOT — AND SCROLLING ON HOVER WAS A REAL DEFECT.**
     * Hovering a row that is only half in view made this effect scroll it fully into view, so moving
     * the mouse toward the bottom of the list threw the list forward under the cursor: *"if I hover
     * towards the end of the list it immediately jumps to the end"*. The highlight still follows the
     * pointer, which is what a pointer is for; what it no longer does is move the page.
     *
     * ⚠️ **A ref, not state.** It is a fact about the last input event rather than about what is on
     * screen, and putting it in state would re-render on every arrow press to say nothing new.
     */
    (0, react_1.useEffect)(() => {
        if (!byKeyboard.current)
            return;
        byKeyboard.current = false;
        const box = list.current;
        if (box === null)
            return;
        const row = box.querySelector(`#${CSS.escape(`${scope}-option-${String(active)}`)}`);
        if (!(row instanceof HTMLElement))
            return;
        const top = row.offsetTop;
        const bottom = top + row.offsetHeight;
        if (top < box.scrollTop)
            box.scrollTop = top;
        else if (bottom > box.scrollTop + box.clientHeight)
            box.scrollTop = bottom - box.clientHeight;
    }, [active, scope]);
    if (!open)
        return null;
    const run = (item) => {
        close();
        item.onSelect?.();
    };
    /* The groups, as a view over the flat filtered list — index carried alongside. */
    const groups = [];
    matches.forEach((item, index) => {
        const name = item.group ?? '';
        const last = groups[groups.length - 1];
        if (last?.name === name)
            last.rows.push({ item, index });
        else
            groups.push({ name, rows: [{ item, index }] });
    });
    return (0, react_dom_1.createPortal)((0, jsx_runtime_1.jsxs)("div", { className: "fixed inset-0 z-[60] flex items-start justify-center p-4 pt-[14vh]", children: [(0, jsx_runtime_1.jsx)("div", { className: "fixed inset-0 bg-[color:var(--scrim)]", "aria-hidden": true, onClick: close }), (0, jsx_runtime_1.jsxs)("div", { ref: ref, role: "dialog", "aria-modal": "true", "aria-label": label, tabIndex: -1, className: "bg-popover text-popover-foreground shadow-modal relative z-10 w-full max-w-[560px] overflow-hidden rounded-xl border outline-none", children: [(0, jsx_runtime_1.jsxs)("div", { className: "flex h-11 items-center gap-2 border-b px-3", children: [(0, jsx_runtime_1.jsx)(icon_1.Icon, { name: "search", size: 15, className: "opacity-50" }), (0, jsx_runtime_1.jsx)("input", { 
                                /* ⚠️ `autoFocus` deliberately: a command palette exists to be typed into the
                                   instant it opens, and anything else costs a Tab. No `jsx-a11y` plugin is in this
                                   config to exempt, so this is a comment — if one is ever added, this is the site. */
                                autoFocus: true, value: query, onChange: (event) => {
                                    setQuery(event.target.value);
                                    setActive(0);
                                    onQueryChange?.(event.target.value);
                                }, placeholder: placeholder, "aria-label": placeholder, role: "combobox", "aria-expanded": true, "aria-controls": `${scope}-list`, "aria-autocomplete": "list", "aria-activedescendant": matches.length > 0 ? `${scope}-option-${String(active)}` : undefined, className: "placeholder:text-muted-foreground flex h-11 w-full bg-transparent text-sm outline-none", onKeyDown: (event) => {
                                    switch (event.key) {
                                        case 'ArrowDown':
                                            event.preventDefault();
                                            byKeyboard.current = true;
                                            setActive((at) => Math.min(matches.length - 1, at + 1));
                                            break;
                                        case 'ArrowUp':
                                            event.preventDefault();
                                            byKeyboard.current = true;
                                            setActive((at) => Math.max(0, at - 1));
                                            break;
                                        case 'Home':
                                            event.preventDefault();
                                            byKeyboard.current = true;
                                            setActive(0);
                                            break;
                                        case 'End':
                                            event.preventDefault();
                                            byKeyboard.current = true;
                                            setActive(matches.length - 1);
                                            break;
                                        case 'Enter': {
                                            const item = matches[active];
                                            if (item !== undefined) {
                                                event.preventDefault();
                                                run(item);
                                            }
                                            break;
                                        }
                                        default:
                                            break;
                                    }
                                } })] }), (0, jsx_runtime_1.jsx)("div", { id: `${scope}-list`, ref: list, role: "listbox", "aria-label": label, className: "max-h-[320px] overflow-y-auto p-1", children: matches.length === 0 ? ((0, jsx_runtime_1.jsx)("div", { className: "text-muted-foreground py-6 text-center text-[13px]", "aria-live": "polite", children: loading && loadingLabel !== undefined ? loadingLabel : emptyLabel(query) })) : (groups.map((group) => ((0, jsx_runtime_1.jsxs)("div", { role: "group", "aria-label": group.name === '' ? undefined : group.name, className: "overflow-hidden p-1", children: [group.name === '' ? null : ((0, jsx_runtime_1.jsx)("div", { className: "text-muted-foreground px-2 py-1.5 text-[11px] font-medium", children: group.name })), group.rows.map(({ item, index }) => ((0, jsx_runtime_1.jsxs)("div", { id: `${scope}-option-${String(index)}`, role: "option", "aria-selected": index === active, onMouseEnter: () => {
                                        setActive(index);
                                    }, onClick: () => {
                                        run(item);
                                    }, className: (0, cn_1.cn)('flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-[13px] outline-none', index === active && 'bg-accent text-accent-foreground'), children: [item.icon === undefined ? null : ((0, jsx_runtime_1.jsx)(icon_1.Icon, { name: item.icon, size: 14, className: "text-muted-foreground" })), (0, jsx_runtime_1.jsx)("span", { className: "truncate", children: item.label }), item.hint === undefined ? null : ((0, jsx_runtime_1.jsx)("span", { className: "text-muted-foreground ml-auto truncate text-[11px]", children: item.hint }))] }, `${item.label}-${String(index)}`)))] }, group.name === '' ? `ungrouped-${String(group.rows[0]?.index ?? 0)}` : group.name)))) })] })] }), document.body);
}
//# sourceMappingURL=command-dialog.js.map